import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";

const Line = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ stats, onJoin }) => {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 800], [0, 110]);
  const yGlow = useTransform(scrollY, [0, 800], [0, -70]);

  return (
    <section data-testid="hero-section" className="relative min-h-[100svh] flex flex-col justify-center px-6 md:px-12 pt-32 pb-16 overflow-hidden">
      <motion.div
        style={{ y: yGlow }}
        className="pointer-events-none absolute -top-40 -right-40 h-[36rem] w-[36rem] rounded-full bg-[radial-gradient(circle_at_center,rgba(30,111,255,0.28),transparent_65%)] blur-3xl"
      />
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center max-w-7xl mx-auto w-full">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            data-testid="hero-kicker"
            className="text-xs md:text-sm uppercase tracking-[0.35em] text-white/60 flex items-center gap-2 mb-6"
          >
            <MapPin className="h-4 w-4 text-volt" /> Aventura, Florida — Opening 10.30.2026
          </motion.p>
          <h1 className="font-display uppercase leading-[0.85] tracking-tight text-[clamp(4.5rem,17vw,12rem)]" data-testid="hero-headline">
            <Line delay={0.05}>3D</Line>
            <Line delay={0.18} className="text-volt">Body</Line>
            <Line delay={0.31}>Scan</Line>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8 }}
            data-testid="hero-subline"
            className="mt-6 font-display uppercase text-xl md:text-3xl tracking-tight text-white/90"
          >
            See what your scale <span className="text-gold">can't show.</span>
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 flex flex-wrap gap-3"
            data-testid="hero-chips"
          >
            {["More than weight", "Deeper insights", "Real results"].map((c) => (
              <span key={c} className="border border-white/15 bg-white/5 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-white/70">
                {c}
              </span>
            ))}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
          >
            <motion.button
              data-testid="hero-join-button"
              onClick={onJoin}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="mt-10 inline-flex items-center gap-3 bg-volt px-8 py-4 font-display uppercase text-lg tracking-wide text-white shadow-[0_0_40px_rgba(30,111,255,0.45)] hover:bg-volt-dark transition-colors"
            >
              {stats.is_full ? "Join the reserve list" : "Reserve your spot — $0 today"}
              <ArrowRight className="h-5 w-5" />
            </motion.button>
            <p className="mt-4 text-[11px] uppercase tracking-[0.2em]">
              <span className="text-gold">$0 today</span><span className="text-white/50"> — $49/month at opening · founder rate locked for life</span>
            </p>
          </motion.div>
        </div>
        <div className="lg:col-span-5 relative">
          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto max-w-sm lg:max-w-none"
          >
            <div className="absolute -inset-10 bg-[radial-gradient(circle_at_center,rgba(30,111,255,0.35),transparent_70%)] blur-2xl" />
            <div className="relative border border-white/15 bg-white/5 p-2 rotate-1 shadow-[0_0_60px_rgba(30,111,255,0.15)]">
              <img
                src="/assets/flyer.png"
                alt="PHYSIMETRY 3D body scan cabin"
                data-testid="hero-flyer-image"
                className="w-full h-auto object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
